import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoBannerCst from "@components/InfoBannerCst";
import {
  Folder,
  Folders,
  GraduationCap,
  ListFilter,
  Search,
  User,
  Users,
  X,
} from "lucide-react";
import InputCst from "@components/InputCst/InputCst";
import { useDebouncedValue } from "@hooks/useDebouncedValue";
// import SelectCst from "@components/InputCst/SelectCst";
import SortMenuCst from "@components/SortMenuCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import { openToast } from "@utils/toastStore";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { CatalogEntityType } from "@pages/Contents/V2/Corporate/Catalog/types";
import { useNormalizedAggregatedItems } from "@pages/Contents/V2/Corporate/Catalog/hooks/useNormalizedAggregatedItems";
import { useLOSearch } from "@pages/Contents/V2/Corporate/Catalog/search/useLOSearch";
import { useLOFilterableFields } from "@pages/Contents/V2/Corporate/Catalog/search/useLOFilterableFields";
import SearchFiltersAccordion from "@pages/Contents/V2/Corporate/Catalog/search/SearchFiltersAccordion";
import {
  buildSearchConditions,
  LO_SORT_OPTION_KEYS,
  LO_SORT_ORDER_BY,
  SEARCH_FILTER_ORDER_BY_TAB,
} from "@pages/Contents/V2/Corporate/Catalog/search/types";
import ConfigurationStep from "@pages/Contents/V2/Corporate/Catalog/Enrollment/steps/ConfigurationStep";
import { useAttributesList } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/hooks/useAttributesList";
import {
  buildInitialConfig,
  getDisabledEnrollTypes,
  validateConfig,
} from "@pages/Contents/V2/Corporate/Catalog/Enrollment/config/enrollmentFieldsConfig";
import {
  EnrollmentConfigState,
  EnrollmentTriggerPayload,
  EnrollType,
} from "@pages/Contents/V2/Corporate/Catalog/Enrollment/types";
import {
  UserLoSelection,
  useUserEnrollmentSubmit,
} from "../hooks/useUserEnrollmentSubmit";
import { enrollGroupToLearningObjects } from "../enrollGroupToLearningObjects";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

const PAGE_SIZE = 10;
const MAX_LOS = 100;
const SEARCH_DEBOUNCE_MS = 400;
const FILTER_FIELD_ORDER = SEARCH_FILTER_ORDER_BY_TAB.all.filter(
  (field) => field !== "standalone",
);
const HIDDEN_STATUS_VALUES = ["draft"];

export type EnrollSubjectType = "user" | "group";

interface EnrollLOModalProps {
  open: boolean;
  onClose: () => void;
  subjectType: EnrollSubjectType;
  subject: any;
  groupType?: "STATIC" | "DYNAMIC";
  corporateId: string;
  onSuccess?: () => void;
  initialSelection?: UserLoSelection[];
  initialStep?: "select" | "config";
  initialConfig?: EnrollmentConfigState;
  extraDisabledEnrollTypes?: EnrollType[];
}

interface ContentProps {
  onClose: () => void;
  subjectType: EnrollSubjectType;
  subject: any;
  groupType?: "STATIC" | "DYNAMIC";
  corporateId: string;
  onSuccess?: () => void;
  initialSelection?: UserLoSelection[];
  initialStep?: "select" | "config";
  initialConfig?: EnrollmentConfigState;
  extraDisabledEnrollTypes?: EnrollType[];
}

const EnrollLOModalContent: React.FC<ContentProps> = ({
  onClose,
  subjectType,
  subject,
  groupType,
  corporateId,
  onSuccess,
  initialSelection,
  initialStep,
  initialConfig,
  extraDisabledEnrollTypes,
}) => {
  const { t } = useTranslation("users-groups-v2");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const { t: tActivity } = useTranslation("wizard-activity");
  const theme = useTheme();

  const [step, setStep] = useState<"select" | "config">(
    initialStep ?? "select",
  );
  const [selection, setSelection] = useState<Map<string, UserLoSelection>>(
    () =>
      new Map((initialSelection ?? []).map((s) => [`${s.kind}-${s.id}`, s])),
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const [accordionFilters, setAccordionFilters] = useState<Record<string, any>>(
    {},
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sort, setSort] = useState("id-desc");
  const [config, setConfig] = useState<EnrollmentConfigState>(
    () => initialConfig ?? buildInitialConfig(),
  );
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const { options: attributes } = useAttributesList(corporateId);
  const { submit, isSubmitting: isSubmittingUser } = useUserEnrollmentSubmit();
  const [isSubmittingGroup, setIsSubmittingGroup] = useState(false);
  const isSubmitting = isSubmittingUser || isSubmittingGroup;

  const { fields: filterableFields } = useLOFilterableFields(corporateId);

  const conditions = useMemo(
    () =>
      buildSearchConditions({
        includeDrafts: false,
        standaloneOnly: true,
        excludeObsolete: true,
        fields: filterableFields,
        filterValues: accordionFilters,
      }),
    [filterableFields, accordionFilters],
  );

  const hasActiveFilters = useMemo(() => {
    const hasValue = (val: any): boolean => {
      if (val === undefined || val === null || val === "") return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === "object") return Object.values(val).some(hasValue);
      return true;
    };
    return Object.values(accordionFilters || {}).some(hasValue);
  }, [accordionFilters]);

  const filtersHighlighted = hasActiveFilters || filtersOpen;

  const { items, totalItems, totalPages, isLoading } = useLOSearch({
    corporateId,
    conditions,
    searchText: search,
    orderBy: LO_SORT_ORDER_BY[sort] ?? LO_SORT_ORDER_BY["id-desc"],
    page,
    pageSize,
    enabled: !!corporateId,
  });

  const normalizedItems = useNormalizedAggregatedItems(items);

  const selectionKey = (kind: CatalogEntityType, id: number) => `${kind}-${id}`;

  const handleToggle = (
    entity: any,
    kind: CatalogEntityType,
    next: boolean,
  ) => {
    setSelection((prev) => {
      const copy = new Map(prev);
      const key = selectionKey(kind, entity.id);
      if (next) {
        if (copy.size >= MAX_LOS) return prev;
        copy.set(key, {
          id: entity.id,
          kind,
          title: entity.title || entity.name || "",
          entity,
        });
      } else {
        copy.delete(key);
      }
      return copy;
    });
  };

  const pageAllSelected =
    normalizedItems.length > 0 &&
    normalizedItems.every(({ entity, entityType }) =>
      selection.has(selectionKey(entityType, entity.id)),
    );

  const handleTogglePage = (next: boolean) => {
    setSelection((prev) => {
      const copy = new Map(prev);
      if (next) {
        for (const { entity, entityType } of normalizedItems) {
          if (copy.size >= MAX_LOS) break;
          copy.set(selectionKey(entityType, entity.id), {
            id: entity.id,
            kind: entityType,
            title: entity.title || entity.name || "",
            entity,
          });
        }
      } else {
        for (const { entity, entityType } of normalizedItems) {
          copy.delete(selectionKey(entityType, entity.id));
        }
      }
      return copy;
    });
  };

  const counters = useMemo(() => {
    let activities = 0;
    let courses = 0;
    let paths = 0;
    selection.forEach((s) => {
      if (s.kind === "course") courses += 1;
      else if (s.kind === "path") paths += 1;
      else activities += 1;
    });
    return { activities, courses, paths };
  }, [selection]);

  const disabledEnrollTypes = useMemo(() => {
    const kinds = new Set<CatalogEntityType>();
    selection.forEach((s) => kinds.add(s.kind));
    const union = new Set<string>();
    kinds.forEach((kind) => {
      getDisabledEnrollTypes({
        objectType: kind,
      } as EnrollmentTriggerPayload).forEach((et) => union.add(et));
    });
    (extraDisabledEnrollTypes ?? []).forEach((et) => union.add(et));
    return Array.from(union) as any[];
  }, [selection, extraDisabledEnrollTypes]);

  const validation = useMemo(() => validateConfig(config), [config]);

  const subjectName =
    subjectType === "group"
      ? subject?.name || subject?.title || "-"
      : [subject?.name, subject?.surname].filter(Boolean).join(" ").trim() ||
        subject?.email ||
        "-";

  const handleSubmit = async () => {
    setAttemptedSubmit(true);
    if (!validation.valid) return;
    const los = Array.from(selection.values());
    try {
      if (subjectType === "group") {
        setIsSubmittingGroup(true);
        try {
          await enrollGroupToLearningObjects({
            corporateId,
            groupId: subject?.id,
            groupType: groupType || "STATIC",
            loSelections: los.map((s) => ({ id: s.id, type: s.kind })),
            enrollType: config.enrollType || "",
            config,
          });
        } finally {
          setIsSubmittingGroup(false);
        }
      } else {
        await submit({
          corporateId,
          userId: subject?.id_user,
          los,
          config,
        });
      }
      onSuccess?.();
      onClose();
      openToast({
        severity: "success",
        message: String(t("enroll-modal.success")),
      });
    } catch (e: any) {
      onClose();
      openToast({
        severity: "error",
        message: e?.message || String(t("enroll-modal.error")),
      });
    }
  };

  const counterItem = (icon: React.ReactNode, label: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {icon}
      <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <>
      <DialogTitle sx={{ padding: "20px 24px 8px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: theme.palette.text.primary,
              }}
            >
              {subjectType === "group" ? (
                <Users size={16} />
              ) : (
                <User size={16} />
              )}
              <Typography sx={{ fontSize: "13px" }}>{subjectName}</Typography>
            </Box>

            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("enroll-modal.title")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
                mt: 1,
                color: theme.palette.text.primary,
              }}
            >
              <Typography sx={{ fontSize: "13px" }}>
                {t("enroll-modal.enrolling-to")}
              </Typography>
              {counterItem(
                <GraduationCap size={15} />,
                `${counters.activities} ${t("enroll-modal.counter-activities")}`,
              )}
              {counterItem(
                <Folder size={15} />,
                `${counters.courses} ${t("enroll-modal.counter-courses")}`,
              )}
              {counterItem(
                <Folders size={15} />,
                `${counters.paths} ${t("enroll-modal.counter-paths")}`,
              )}
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isSubmitting}
            sx={{ color: theme.palette.text.primary, flexShrink: 0 }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "0 24px 8px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        <Box sx={{ pt: "16px" }}>
          {step === "select" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <InfoBannerCst>
                {t(
                  subjectType === "group"
                    ? "enroll-modal.max-banner-group"
                    : "enroll-modal.max-banner",
                  { max: MAX_LOS },
                )}
              </InfoBannerCst>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: "180px",
                    "& .MuiFormHelperText-root": { display: "none" },
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  <InputCst
                    id="user-enroll-search"
                    placeholder={t("enroll-modal.search-placeholder") || ""}
                    inputSearch
                    value={searchInput}
                    onChange={(e: any) => {
                      setSearchInput(e.target.value);
                      setPage(1);
                    }}
                    startAdornment={
                      <Search
                        size={16}
                        color={theme.palette.systemSecondary01}
                        style={{ marginRight: 8 }}
                      />
                    }
                  />
                </Box>
                {/* TODO: quick filter Stato rimosso con il passaggio a
                    OpenSearch
                <SelectCst
                  id="user-enroll-status"
                  label={t("enroll-modal.status.label") || ""}
                  value={status}
                  options={[...]}
                  onChange={(v: any) => {
                    setStatus(v || "");
                    setPage(1);
                  }}
                  customWidth="160px"
                />
                */}
                <ButtonSecondary
                  size="md"
                  startIcon={<ListFilter size={16} />}
                  onClick={() => setFiltersOpen((prev) => !prev)}
                  sxButton={
                    filtersHighlighted
                      ? {
                          borderColor: theme.palette.systemSecondary01,
                        }
                      : undefined
                  }
                >
                  {tCatalog("filters-button")}
                </ButtonSecondary>
                <SortMenuCst
                  id="user-enroll-sort"
                  value={sort}
                  options={LO_SORT_OPTION_KEYS.map((key) => ({
                    value: key,
                    label: String(tCatalog(`sort.${key}`)),
                  }))}
                  onChange={(v) => {
                    setSort(v || "id-desc");
                    setPage(1);
                  }}
                />
              </Box>

              <SearchFiltersAccordion
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                fields={filterableFields}
                fieldOrder={FILTER_FIELD_ORDER}
                hiddenStatusValues={HIDDEN_STATUS_VALUES}
                value={accordionFilters}
                onApply={(next) => {
                  setAccordionFilters(next);
                  setPage(1);
                }}
                columns={2}
              />

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Checkbox
                  checked={pageAllSelected}
                  indeterminate={selection.size > 0 && !pageAllSelected}
                  onChange={(e) => handleTogglePage(e.target.checked)}
                  sx={{
                    padding: "4px",
                    color: theme.palette.divider,
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
                <Typography
                  sx={{ fontSize: "13px", color: theme.palette.text.primary }}
                >
                  {t("enroll-modal.selected-count", {
                    count: selection.size,
                  })}
                </Typography>
              </Box>

              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : normalizedItems.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 5,
                    color: theme.palette.text.primary,
                  }}
                >
                  {t("enroll-modal.empty")}
                </Box>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  {normalizedItems.map(({ entity, entityType }) => {
                    const cardProps = mapEntityToCard(entity, entityType, {
                      t: tCatalog,
                      tActivity,
                      onDetails: () => {},
                      hideStandaloneBadge: true,
                    });
                    const key = selectionKey(entityType, entity.id);
                    return (
                      <ListItemCardCst
                        key={key}
                        {...cardProps}
                        actions={[]}
                        selectable
                        selected={selection.has(key)}
                        onSelect={(next) =>
                          handleToggle(entity, entityType, next)
                        }
                      />
                    );
                  })}
                </Box>
              )}

              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <PaginatorCst
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    onPageChange={setPage}
                    onPageSizeChange={(next) => {
                      setPageSize(next);
                      setPage(1);
                    }}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <ConfigurationStep
              trigger={null}
              config={config}
              onChange={setConfig}
              attributes={attributes}
              errors={attemptedSubmit ? validation.errors : {}}
              disabledEnrollTypes={disabledEnrollTypes}
              hiddenEnrollTypes={initialConfig ? undefined : ["AUTO", "BOTTOM"]}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        {step === "config" && (
          <ButtonSecondary
            size="md"
            onClick={() => setStep("select")}
            disabled={isSubmitting}
          >
            {t("enroll-modal.back")}
          </ButtonSecondary>
        )}
        <Box sx={{ flex: 1 }} />
        {step === "select" ? (
          <ButtonPrimary
            size="md"
            onClick={() => setStep("config")}
            disabled={selection.size === 0}
          >
            {t("enroll-modal.next")}
          </ButtonPrimary>
        ) : (
          <ButtonPrimary
            size="md"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t("enroll-modal.submitting")
              : t("enroll-modal.submit")}
          </ButtonPrimary>
        )}
      </DialogActions>
    </>
  );
};

const EnrollLOModal: React.FC<EnrollLOModalProps> = ({
  open,
  onClose,
  subjectType,
  subject,
  groupType,
  corporateId,
  onSuccess,
  initialSelection,
  initialStep,
  initialConfig,
  extraDisabledEnrollTypes,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {open && (
        <EnrollLOModalContent
          onClose={onClose}
          subjectType={subjectType}
          subject={subject}
          groupType={groupType}
          corporateId={corporateId}
          onSuccess={onSuccess}
          initialSelection={initialSelection}
          initialStep={initialStep}
          initialConfig={initialConfig}
          extraDisabledEnrollTypes={extraDisabledEnrollTypes}
        />
      )}
    </Dialog>
  );
};

export default EnrollLOModal;
